// src/App.js
import { CircularProgress, createTheme, useMediaQuery } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import React, {
  Suspense,
  useCallback,
  useEffect, // <-- Asegúrate de que useMemo esté importado
  useRef,
  useState,
} from "react";
import { ErrorBoundary } from "react-error-boundary";
import { HelmetProvider } from "react-helmet-async";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

// --- CONTEXTOS ---
import {
  AccessibilityProvider,
  useAccessibility, // <-- Importar hook de accesibilidad
} from "./context/AccessibilityContext";
import {
  SubscriptionProvider,
  useSubscription,
} from "./context/SubscriptionProvider";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
// --- FIN CONTEXTOS ---

import { isUserAdmin } from "./services/firebase";

// Componentes
import ChatbotButton from "./components/chatbot/ChatbotButton";
import ChatbotWindow from "./components/chatbot/ChatbotWindow";
import CookieBar from "./components/common/CookieBar";
import Footer from "./components/common/Footer";
import Header from "./components/common/Header";
import LoadingComponent from "./components/common/LoadingSpinner";
import ScrollToTop from "./components/common/ScrollToTop";
import SkipLink from "./components/common/SkipLink";

// Páginas estáticas (si alguna no es lazy)
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";

// Páginas con carga diferida (Lazy Loading)
const Blog = React.lazy(() => import("./pages/Blog"));
const BlogPost = React.lazy(() => import("./pages/BlogPost"));
const Chatbot = React.lazy(() => import("./pages/Chatbot"));
const Contact = React.lazy(() => import("./pages/Contact"));
const Forum = React.lazy(() => import("./pages/Forum"));
const Info = React.lazy(() => import("./pages/Info")); // Info ya está aquí
// ArticleDetailWrapper y ArticleDetail se pueden mantener si se usan en otro lugar,
// pero ArticleDetailWrapper no se usará para esta ruta.
// const ArticleDetail = React.lazy(() => import('./components/info/ArticleDetail')); // Esta importación es para el componente de UI, no el wrapper
const Login = React.lazy(() => import("./pages/Login"));
const Register = React.lazy(() => import("./pages/Register"));
const Specialists = React.lazy(() => import("./pages/Specialists"));
const SpecialistDetail = React.lazy(
  () => import("./components/specialists/SpecialistDetail"),
);
const Subscription = React.lazy(() => import("./pages/Subscription"));
const PaymentErrorPage = React.lazy(() => import("./pages/PaymentErrorPage"));
const UserProfile = React.lazy(() => import("./pages/UserProfile"));
const Admin = React.lazy(() => import("./pages/Admin"));
const CookiePolicy = React.lazy(() => import("./pages/CookiePolicy"));
const Privacy = React.lazy(() => import("./pages/Privacy"));
const Terms = React.lazy(() => import("./pages/Terms"));
const ForumThread = React.lazy(() => import("./components/forum/ForumThread"));
const PostDetail = React.lazy(() => import("./components/forum/PostDetail"));
const NewPostForm = React.lazy(() => import("./components/forum/NewPostForm"));
const ResourcesArt = React.lazy(() => import("./pages/ResourcesArt"));
const CommunityGuidelines = React.lazy(
  () => import("./pages/CommunityGuidelines"),
);
const DisclaimerPage = React.lazy(() => import("./pages/DisclaimerPage"));
const AccessibilityStatement = React.lazy(
  () => import("./pages/AccessibilityStatement"),
);
const Cookies = React.lazy(() => import("./pages/Cookies"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));

// --- COMPONENTES AUXILIARES (ProtectedRoute, AdminProtectedRoute, ErrorFallback, SuspenseFallback, ArticleDetailWrapper) ---
// Mantener estos componentes tal como estaban en tu archivo original

// Componente para rutas protegidas (requiere autenticación)
const ProtectedRoute = ({ children }) => {
  const { currentUser, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingComponent />;
  }

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Componente para rutas protegidas de administración (requiere autenticación y rol admin)
const AdminProtectedRoute = ({ children }) => {
  const { currentUser, isLoading } = useAuth();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!currentUser) {
        setChecking(false);
        return;
      }

      try {
        const adminStatus = await isUserAdmin(currentUser.uid);
        setIsAdmin(adminStatus);
      } catch (error) {
        console.error("Error verificando rol de administrador:", error);
      } finally {
        setChecking(false);
      }
    };

    checkAdminStatus();
  }, [currentUser]);

  if (isLoading || checking) {
    return <LoadingComponent />;
  }

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Componente para mostrar errores genéricos - Mejorado con accesibilidad
const ErrorFallback = ({ error, resetErrorBoundary }) => {
  useEffect(() => {
    console.error("Error capturado por ErrorBoundary:", error);
  }, [error]);

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        padding: "20px",
        textAlign: "center",
        maxWidth: "800px",
        margin: "0 auto",
        marginTop: "2rem",
      }}
    >
      <h2 tabIndex={-1}>Ha ocurrido un problema</h2>
      <p>Lo sentimos, algo no ha funcionado correctamente.</p>
      <pre
        style={{
          textAlign: "left",
          whiteSpace: "pre-wrap",
          color: "red",
          backgroundColor: "#ffeeee",
          padding: "1rem",
          borderRadius: "4px",
          overflow: "auto",
          maxHeight: "200px",
        }}
      >
        {error.message}
      </pre>
      <button
        onClick={resetErrorBoundary}
        style={{
          padding: "10px 20px",
          backgroundColor: "#3A8EBA",
          color: "white",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer",
          fontSize: "16px",
          marginTop: "10px",
        }}
        aria-label="Intentar cargar de nuevo la aplicación"
      >
        Intentar de nuevo
      </button>
    </div>
  );
};

// Componente de carga para Suspense con transiciones suaves y accesibilidad
const SuspenseFallback = React.memo(() => {
  const isMountedRef = useRef(true);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isMountedRef.current) {
        setVisible(true);
      }
    }, 300);

    return () => {
      isMountedRef.current = false;
      clearTimeout(timer);
    };
  }, []);

  return (
    <div
      role="progressbar"
      aria-label="Cargando contenido"
      aria-busy="true"
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        width: "100vw",
        position: "fixed",
        top: 0,
        left: 0,
        backgroundColor: "rgba(255, 255, 255, 0.7)",
        zIndex: 9999,
        opacity: visible ? 1 : 0,
        transition: "opacity 0.3s ease-in-out",
      }}
    >
      <CircularProgress size={60} thickness={4} />
      <span className="sr-only">Cargando contenido, por favor espere</span>
    </div>
  );
});
SuspenseFallback.displayName = "SuspenseFallback";

// --- NUEVO COMPONENTE WRAPPER PARA DETALLE DE ARTÍCULOS ---
// const ArticleDetailWrapper = () => { ... }
// ESTE COMPONENTE YA NO SERÁ USADO POR LA RUTA /info/:id
// Puedes comentarlo o eliminarlo si no se usa en ningún otro sitio.
// Por seguridad, primero coméntalo. Si todo funciona, puedes considerar eliminarlo.
/*
const ArticleDetailWrapper = () => {
  const { id } = useParams();
  const { i18n, t } = useTranslation();
  const [articleData, setArticleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticleById = async () => {
      setLoading(true);
      setError(null);
      setArticleData(null);

      // ESTA LÓGICA SE MOVERÁ EFECTIVAMENTE A Info.js
      const apiUrl = `/api/down-syndrome-info/${id}?language=${i18n.language}`;

      try {
        const response = await fetch(apiUrl);

        if (!response.ok) {
          let errorMsg = `Error ${response.status}: ${response.statusText}`;
          try {
            const errorBody = await response.json();
            errorMsg = errorBody.message || errorMsg;
          } catch (e) {
            // Ignorar
          }

          if (response.status === 404) {
            setError('Artículo no encontrado.');
          } else {
            setError(errorMsg);
          }
          throw new Error(errorMsg);
        }

        const data = await response.json();

        if (data && data.article) {
          setArticleData(data.article);
        } else {
          setError('No se recibieron datos válidos del artículo.');
          console.warn('Respuesta inesperada de la API:', data);
        }
      } catch (err) {
        console.error(`Error fetching article ${id}:`, err);
        setError(err.message || 'No se pudo cargar el artículo.');
      } finally {
        setLoading(false);
      }
    };

    fetchArticleById();
  }, [id, i18n.language]);

  if (loading) {
    return <LoadingComponent />;
  }

  if (error) {
    return (
      <Box sx={{ padding: 4, textAlign: 'center' }}>
        <Alert severity="error" sx={{ justifyContent: 'center' }}>
          {error === 'Artículo no encontrado.'
            ? t(
                'info.articleNotFound',
                'Lo sentimos, el artículo que buscas no existe o fue eliminado.',
              )
            : t('info.errorLoading', `Error al cargar el contenido: ${error}`)}
        </Alert>
      </Box>
    );
  }

  if (!articleData) {
    console.warn(`Article data for ${id} is null after loading without error.`);
    return <NotFound />;
  }

  return (
    <ArticleDetail
      title={articleData.title}
      content={articleData.content}
      category={articleData.category}
      date={
        articleData.createdAt
          ? new Date(articleData.createdAt).toLocaleDateString(i18n.language, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })
          : ''
      }
      image={articleData.imageUrl}
      tags={articleData.tags || []}
      loading={false}
    />
  );
};
*/

// Componente que contiene Header, Rutas y Chatbot (Memoizado)
const MainContent = React.memo(
  ({
    toggleTheme,
    mode,
    chatbotWindowOpen,
    setChatbotWindowOpen,
    hasUnreadMessages,
    setHasUnreadMessages,
  }) => {
    const {
      isSubscribed,
      isAdmin,
      currentUser: subCurrentUser,
    } = useSubscription(); // Renombrar currentUser para evitar colisión si usas useAuth() aquí también
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const navigate = useNavigate();
    const { currentUser: authCurrentUser } = useAuth(); // currentUser del AuthContext
    const mainContentRef = useRef(null);
    const location = useLocation();

    // LOG PARA EL COMPONENTE CONSUMIDOR
    console.log("[LOG 10 - MainContent] Subscription context received:", {
      isSubscribed,
      isAdmin,
      subCurrentUser: subCurrentUser
        ? { uid: subCurrentUser.uid, email: subCurrentUser.email }
        : null,
    }); // LOG 10

    const handleNavigateToChatbotPage = useCallback(() => {
      navigate("/chatbot");
    }, [navigate]);

    const handleOpenChatWindow = useCallback(() => {
      if (isSubscribed) {
        setChatbotWindowOpen(true);
        setHasUnreadMessages(false);

        const announcement = document.createElement("div");
        announcement.setAttribute("role", "status");
        announcement.setAttribute("aria-live", "polite");
        announcement.className = "sr-only";
        announcement.innerText = "Chatbot abierto";
        document.body.appendChild(announcement);
        setTimeout(() => document.body.removeChild(announcement), 1000);
      } else {
        navigate("/subscriptions");
      }
    }, [isSubscribed, setChatbotWindowOpen, setHasUnreadMessages, navigate]);

    useEffect(() => {
      if (isSubscribed) {
        try {
          localStorage.setItem(
            "chatbotWindowState",
            chatbotWindowOpen ? "open" : "closed",
          );
        } catch (e) {
          console.error("Error saving chatbot state to localStorage:", e);
        }
      }
    }, [isSubscribed, chatbotWindowOpen]);

    useEffect(() => {
      if (isSubscribed) {
        try {
          // Comentado para evitar apertura automática al cargar
          // if (savedState === 'open') {
          //   setTimeout(() => setChatbotWindowOpen(true), 500);
          // }
        } catch (e) {
          console.error("Error reading chatbot state from localStorage:", e);
        }
      }
    }, [isSubscribed, setChatbotWindowOpen]);

    useEffect(() => {
      // Limpiar título al desmontar (buena práctica)
      return () => {
        document.title = "VicDown - Apoyo para familias con síndrome de Down";
      };
    }, []);

    const hideChatRoutes = ["/login", "/register"];
    const shouldHideChat = hideChatRoutes.includes(location.pathname);

    return (
      <>
        <SkipLink targetId="main-content" />

        <Header
          toggleTheme={toggleTheme}
          mode={mode}
          onChatOpen={handleNavigateToChatbotPage}
          user={authCurrentUser}
        />

        <main
          id="main-content"
          ref={mainContentRef}
          style={{ flexGrow: 1, minHeight: "70vh" }}
          tabIndex={-1}
        >
          <Routes>
            {/* --- Definición de todas las rutas --- */}
            <Route
              path="/"
              element={<Home toggleTheme={toggleTheme} mode={mode} />}
            />
            <Route
              path="/blog"
              element={<Blog toggleTheme={toggleTheme} mode={mode} />}
            />
            <Route
              path="/blog/:slug"
              element={<BlogPost toggleTheme={toggleTheme} mode={mode} />}
            />
            <Route
              path="/chatbot"
              element={<Chatbot toggleTheme={toggleTheme} mode={mode} />}
            />
            <Route
              path="/forum"
              element={<Forum toggleTheme={toggleTheme} mode={mode} />}
            />
            <Route
              path="/forum/category/:id"
              element={<ForumThread toggleTheme={toggleTheme} mode={mode} />}
            />
            <Route
              path="/forum/post/:id"
              element={<PostDetail toggleTheme={toggleTheme} mode={mode} />}
            />
            <Route
              path="/forum/new"
              element={
                <ProtectedRoute>
                  <NewPostForm toggleTheme={toggleTheme} mode={mode} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/specialists"
              element={<Specialists toggleTheme={toggleTheme} mode={mode} />}
            />
            <Route
              path="/specialists/:specialistId"
              element={
                <SpecialistDetail toggleTheme={toggleTheme} mode={mode} />
              }
            />
            <Route
              path="/contact"
              element={<Contact toggleTheme={toggleTheme} mode={mode} />}
            />
            <Route
              path="/info"
              element={<Info toggleTheme={toggleTheme} mode={mode} />}
            />
            <Route
              path="/info/:articleId" // <--- ASEGÚRATE DE QUE ESTÉ ASÍ
              element={<Info toggleTheme={toggleTheme} mode={mode} />}
            />
            <Route
              path="/login"
              element={<Login toggleTheme={toggleTheme} mode={mode} />}
            />
            <Route
              path="/register"
              element={<Register toggleTheme={toggleTheme} mode={mode} />}
            />
            <Route
              path="/reset-password"
              element={<ResetPassword toggleTheme={toggleTheme} mode={mode} />}
            />
            <Route
              path="/subscriptions"
              element={
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <Subscription toggleTheme={toggleTheme} mode={mode} />
                </ErrorBoundary>
              }
            />
            <Route
              path="/payment-error"
              element={
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <PaymentErrorPage toggleTheme={toggleTheme} mode={mode} />
                </ErrorBoundary>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <UserProfile toggleTheme={toggleTheme} mode={mode} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/*"
              element={
                <AdminProtectedRoute>
                  <Admin toggleTheme={toggleTheme} mode={mode} />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/cookie-policy"
              element={<CookiePolicy toggleTheme={toggleTheme} mode={mode} />}
            />
            <Route
              path="/privacy"
              element={<Privacy toggleTheme={toggleTheme} mode={mode} />}
            />
            <Route
              path="/terms"
              element={<Terms toggleTheme={toggleTheme} mode={mode} />}
            />
            <Route
              path="/resources-art"
              element={<ResourcesArt toggleTheme={toggleTheme} mode={mode} />}
            />
            <Route
              path="/community-guidelines"
              element={
                <CommunityGuidelines toggleTheme={toggleTheme} mode={mode} />
              }
            />
            <Route
              path="/disclaimer"
              element={<DisclaimerPage toggleTheme={toggleTheme} mode={mode} />}
            />
            <Route
              path="/accessibility"
              element={
                <AccessibilityStatement toggleTheme={toggleTheme} mode={mode} />
              }
            />
            <Route path="/cookies" element={<Cookies />} />
            <Route
              path="*"
              element={<NotFound toggleTheme={toggleTheme} mode={mode} />}
            />
          </Routes>
        </main>

        <Footer mode={mode} />

        {isSubscribed && !shouldHideChat && (
          <>
            <ChatbotWindow
              isOpen={chatbotWindowOpen}
              onClose={() => setChatbotWindowOpen(false)}
              onMinimize={() => setChatbotWindowOpen(false)}
              onNewMessage={() =>
                !chatbotWindowOpen && setHasUnreadMessages(true)
              }
            />
            <ChatbotButton
              onClick={handleOpenChatWindow}
              position={{
                bottom: isMobile ? 20 : 30,
                right: isMobile ? 20 : 30,
              }}
              size={isMobile ? 55 : 65}
              showBadge={hasUnreadMessages && !chatbotWindowOpen}
              ariaLabel="Abrir ventana de chat"
            />
          </>
        )}
      </>
    );
  },
);
MainContent.displayName = "MainContent";

// ... (imports y otros componentes auxiliares como ProtectedRoute, AdminProtectedRoute, ErrorFallback, SuspenseFallback, ArticleDetailWrapper, MainContent) ...

// *** NUEVO COMPONENTE HIJO QUE CONTIENE LA LÓGICA PRINCIPAL Y EL THEMEPROVIDER ***
const AppContent = () => {
  // Estado para el tema (claro/oscuro) con persistencia
  const [mode, setMode] = useState(() => {
    try {
      const savedTheme = localStorage.getItem("theme");
      if (savedTheme) return savedTheme;
      const prefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)",
      ).matches;
      return prefersDark ? "dark" : "light";
    } catch (e) {
      console.error("Error reading theme from localStorage:", e);
      return "light";
    }
  });

  // Estado para la ventana del chatbot (premium)
  const [chatbotWindowOpen, setChatbotWindowOpen] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);

  // *** Consumir el contexto de accesibilidad ***
  const { settings = {} } = useAccessibility(); // <-- ASÍ DEBE QUEDAR

  // Función memoizada para cambiar el tema
  const toggleTheme = useCallback(() => {
    setMode((prevMode) => {
      const newMode = prevMode === "light" ? "dark" : "light";
      try {
        localStorage.setItem("theme", newMode);
        const announcement = document.createElement("div");
        announcement.setAttribute("role", "status");
        announcement.setAttribute("aria-live", "polite");
        announcement.className = "sr-only";
        announcement.innerText = `Modo ${
          newMode === "dark" ? "oscuro" : "claro"
        } activado`;
        document.body.appendChild(announcement);
        setTimeout(() => document.body.removeChild(announcement), 1000);
      } catch (e) {
        console.error("Error saving theme to localStorage:", e);
      }
      return newMode;
    });
  }, []);

  // *** Crear el tema de Material UI BASADO EN MODO Y SETTINGS ***
  const theme = React.useMemo(
    () => {
      // Paleta base según el modo (asumiendo que basePalette se define antes o aquí)
      const basePalette = {
        mode,
        primary: {
          main: mode === "light" ? "#3A8EBA" : "#58a6ff",
          light: mode === "light" ? "#62b5e5" : "#7db9ff",
          dark: mode === "light" ? "#2c6a8c" : "#3a7cb2",
          contrastText: "#ffffff",
        },
        secondary: {
          main: mode === "light" ? "#ff8f00" : "#ffa726",
          light: mode === "light" ? "#ffb74d" : "#ffcc80",
          dark: mode === "light" ? "#c56000" : "#c67c00",
          contrastText: "#000000",
        },
        background: {
          default: mode === "light" ? "#f8faff" : "#0f1a2e",
          paper: mode === "light" ? "#ffffff" : "#162240",
        },
        error: { main: "#f44336", light: "#e57373", dark: "#d32f2f" },
        warning: { main: "#ff9800", light: "#ffb74d", dark: "#f57c00" },
        info: { main: "#2196f3", light: "#64b5f6", dark: "#1976d2" },
        success: { main: "#4caf50", light: "#81c784", dark: "#388e3c" },
        text: {
          primary: mode === "light" ? "#24292e" : "#c9d1d9",
          secondary: mode === "light" ? "#586069" : "#8b949e",
          disabled:
            mode === "light"
              ? "rgba(0, 0, 0, 0.38)"
              : "rgba(255, 255, 255, 0.5)",
        },
      };

      // Modificar paleta si highContrast está activo (EJEMPLO - AJUSTAR COLORES)
      const finalPalette = settings.highContrast
        ? {
            mode,
            primary: {
              main: mode === "light" ? "#000000" : "#FFFF00",
              contrastText: mode === "light" ? "#ffffff" : "#000000",
            },
            secondary: {
              main: mode === "light" ? "#0000FF" : "#00FFFF",
              contrastText: "#000000",
            },
            background: {
              default: mode === "light" ? "#ffffff" : "#000000",
              paper: mode === "light" ? "#f0f0f0" : "#1a2a44",
            },
            text: {
              primary: mode === "light" ? "#000000" : "#ffffff",
              secondary: mode === "light" ? "#333333" : "#dddddd",
            },
            error: {
              main: mode === "light" ? "#FF0000" : "#FF5555",
              contrastText: "#ffffff",
            },
            warning: {
              main: mode === "light" ? "#FFA500" : "#FFD700",
              contrastText: "#000000",
            },
            info: {
              main: mode === "light" ? "#0000FF" : "#ADD8E6",
              contrastText: mode === "light" ? "#ffffff" : "#000000",
            },
            success: {
              main: mode === "light" ? "#008000" : "#90EE90",
              contrastText: mode === "light" ? "#ffffff" : "#000000",
            },
          }
        : basePalette;

      // Calcular multiplicador de fuente
      const fontSizeMultiplier =
        settings.fontSize === "large"
          ? 1.2
          : settings.fontSize === "small"
            ? 0.9
            : 1;
      // Calcular multiplicador de espaciado de línea (si se usa en el contexto)
      const lineHeightMultiplier = settings.lineSpacing || 1; // Asume 1 si no está definido

      return createTheme({
        palette: finalPalette,
        typography: {
          fontFamily:
            "'Poppins', 'Quicksand', 'Roboto', 'Helvetica', 'Arial', sans-serif",
          // Aplicar multiplicador al tamaño base global
          fontSize: 14 * fontSizeMultiplier,
          // Aplicar multiplicador de espaciado de línea globalmente (opcional)
          lineHeight: 1.5 * lineHeightMultiplier, // Ajusta el valor base 1.5 si es diferente
          // Ajustar tamaños específicos si es necesario (ejemplo h1)
          h1: {
            fontWeight: 700,
            fontSize: `${2.5 * fontSizeMultiplier}rem`,
            lineHeight: 1.2 * lineHeightMultiplier,
            letterSpacing: "-0.01562em",
            "@media (min-width:600px)": {
              fontSize: `${3.5 * fontSizeMultiplier}rem`,
            },
          },
          h2: {
            fontWeight: 600,
            fontSize: `${2 * fontSizeMultiplier}rem`,
            lineHeight: 1.3 * lineHeightMultiplier,
            letterSpacing: "-0.00833em",
            "@media (min-width:600px)": {
              fontSize: `${2.75 * fontSizeMultiplier}rem`,
            },
          },
          h3: {
            fontWeight: 600,
            fontSize: `${1.5 * fontSizeMultiplier}rem`,
            lineHeight: 1.4 * lineHeightMultiplier,
            letterSpacing: "0em",
            "@media (min-width:600px)": {
              fontSize: `${2.2 * fontSizeMultiplier}rem`,
            },
          },
          h4: {
            fontWeight: 600,
            fontSize: `${1.25 * fontSizeMultiplier}rem`,
            lineHeight: 1.5 * lineHeightMultiplier,
            letterSpacing: "0.00735em",
            "@media (min-width:600px)": {
              fontSize: `${1.5 * fontSizeMultiplier}rem`,
            },
          },
          button: { textTransform: "none", fontWeight: 600 },
          // ... otros estilos de tipografía existentes ...
        },
        shape: { borderRadius: 8 },
        breakpoints: {
          values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 },
        },
        // Modificar transiciones si animations está desactivado
        transitions:
          settings.animations === false
            ? {
                create: () => "none",
                duration: { enteringScreen: 0, leavingScreen: 0 },
              }
            : undefined, // Usar las transiciones por defecto
        components: {
          // Mantener los overrides existentes
          MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
              root: { padding: "8px 16px", borderRadius: "8px" },
              containedPrimary: {
                "&:hover": {
                  backgroundColor: mode === "light" ? "#2c6a8c" : "#3a7cb2",
                },
              },
            },
          },
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                scrollbarWidth: "thin",
                "&::-webkit-scrollbar": { width: "8px", height: "8px" },
                "&::-webkit-scrollbar-track": {
                  background: mode === "light" ? "#f1f1f1" : "#142036",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: mode === "light" ? "#888" : "#555",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: mode === "light" ? "#555" : "#777",
                },
              },
              ".sr-only": {
                position: "absolute",
                width: "1px",
                height: "1px",
                padding: "0",
                margin: "-1px",
                overflow: "hidden",
                clip: "rect(0, 0, 0, 0)",
                whiteSpace: "nowrap",
                borderWidth: "0",
              },
              a: {
                textDecoration: "none",
                color: finalPalette.primary.main,
                "&:hover": { textDecoration: "underline" },
                "&:focus-visible": {
                  outline: `2px solid ${finalPalette.primary.main}`,
                  outlineOffset: "2px",
                  borderRadius: "2px",
                },
              },
            },
          },
          // ... otros overrides existentes ...
        },
      });
    },
    // *** Añadir settings a las dependencias ***
    [mode, settings],
  );

  // Aplicar el esquema de color al HTML
  useEffect(() => {
    document.documentElement.style.colorScheme = mode;
    document.documentElement.setAttribute("data-theme", mode);
  }, [mode]);

  // Mejorar la accesibilidad con atributos de idioma y viewport
  useEffect(() => {
    document.documentElement.lang = "es";
    const metaViewport = document.querySelector('meta[name="viewport"]');
    if (!metaViewport) {
      const meta = document.createElement("meta");
      meta.name = "viewport";
      meta.content = "width=device-width, initial-scale=1.0";
      document.head.appendChild(meta);
    }
  }, []);

  // *** JSX Principal movido aquí ***
  return (
    <ThemeProvider theme={theme}>
      {" "}
      {/* <-- Usa el theme dinámico */}
      <CssBaseline enableColorScheme />
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <HelmetProvider>
          {/* SubscriptionProvider y Router ahora están dentro de AppContent */}
          <SubscriptionProvider>
            <Router>
              <Suspense fallback={<SuspenseFallback />}>
                <ScrollToTop />
                {/* MainContent renderiza Header, Routes, Footer, Chatbot */}
                <MainContent
                  toggleTheme={toggleTheme}
                  mode={mode}
                  chatbotWindowOpen={chatbotWindowOpen}
                  setChatbotWindowOpen={setChatbotWindowOpen}
                  hasUnreadMessages={hasUnreadMessages}
                  setHasUnreadMessages={setHasUnreadMessages}
                />
                <CookieBar />
              </Suspense>
            </Router>
          </SubscriptionProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}; // <-- Fin de AppContent

// *** Componente App original ahora solo renderiza los Providers externos y AppContent ***
const App = () => {
  return (
    // *** CORRECCIÓN: AuthProvider envuelve a AccessibilityProvider ***
    <AuthProvider>
      <AccessibilityProvider>
        {/* Otros providers globales como SnackbarProvider pueden ir aquí si no dependen de Auth o Accessibility */}
        {/* <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}> */}
        <AppContent /> {/* Componente hijo con ThemeProvider, Router, etc. */}
        {/* </SnackbarProvider> */}
      </AccessibilityProvider>
    </AuthProvider>
  );
};

export default App;
